import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
	Row,
	Button,
	Col,
	Table,
	Form,
	Select,
	notification,
	Badge,
	Input,
	Flex,
	Card,
} from "antd";
import dayjs from "dayjs";
import { debounce } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { GET } from "../../../providers/useAxiosQuery";
import {
	checkNetworkStatus,
	checkPermissions,
	readFile,
	setFileStorage,
} from "../../../providers/useCapacitorStorage";
import { role } from "../../../providers/companyInfo";
import { useTableScrollOnTop } from "../../../providers/CustomTableFilter";
import ModalFormUpdateStatus from "./components/ModalFormUpdateStatus";
import ModalFormFacultyLoad from "./components/ModalFormFacultyLoad";
import PageFacultyMonitoringContext from "./components/PageFacultyMonitoringContext";

export default function PageFacultyMonitoring() {
	const [form] = Form.useForm();

	const location = useLocation();
	const navigation = useNavigate();

	const [dataFaculty, setDataFaculty] = useState([]);
	const [dataFacultyLoadSchedule, setDataFacultyLoadSchedule] = useState([]);
	const [dataFacultyLoadScheduleFiltered, setDataFacultyLoadScheduleFiltered] =
		useState([]);
	const [dataDaySchedules, setDataDaySchedules] = useState([]);
	const [dataSchoolYear, setDataSchoolYear] = useState([]);
	const [dataSemester, setDataSemester] = useState([]);
	const [dataBuildings, setDataBuildings] = useState([]);
	const [dataFloors, setDataFloors] = useState([]);
	const [dataRooms, setDataRooms] = useState([]);
	const [filteredRooms, setFilteredRooms] = useState([]);
	const [dataStatus, setDataStatus] = useState([]);
	const [dataFacultyLoadScheduleStatus, setDataFacultyLoadScheduleStatus] =
		useState([]);

	const [toggleModalUpdateStatus, setToggleModalUpdateStatus] = useState({
		open: false,
		data: null,
	});
	const [toggleModalFormFaculty, setToggleModalFormFaculty] = useState({
		open: false,
		data: null,
	});

	const [tableFilter, setTableFilter] = useState({
		page: 1,
		page_size: 50,
		search: "",
		sort_field: "",
		sort_order: "",
		from: "page_monitoring",
		building_id: "",
		floor_id: "",
		room_id: "",
		meridian: "",
	});

	useEffect(() => {
		let tableFilterTemp = localStorage.getItem("tableFilter");

		let meridian = dayjs().format("A");

		if (tableFilterTemp) {
			tableFilterTemp = JSON.parse(tableFilterTemp);

			tableFilterTemp.meridian = tableFilterTemp.meridian
				? tableFilterTemp.meridian
				: meridian;

			setTableFilter(tableFilterTemp);
		} else {
			setTableFilter((ps) => ({
				...ps,
				meridian,
			}));
		}

		return () => {};
		// eslint-disable-next-line
	}, []);

	GET(
		`api/profile_faculty_load_schedule?from=FacultyMonitoringAttendance&roles=3,4,5`,
		"profile_list",
		(res) => {
			if (res && res.data) {
				let data = res.data.map((faculty) => ({
					value: faculty.id,
					label: `(${faculty.school_id}) ${faculty.fullname}`,
				}));

				setDataFaculty(data);

				checkPermissions().then((res1) => {
					if (res1) {
						if (res1.publicStorage === "granted") {
							setFileStorage({
								foldername: "faculty_load_attendance",
								filename: "profile_list",
								data,
								override: true,
							});
						}
					}
				});
			}
		},
		false
	);

	GET(
		`api/faculty_load_schedule?from=page_monitoring`,
		"faculty_load_schedule_list",
		(res) => {
			if (res && res.data) {
				let data = res.data;

				setDataFacultyLoadSchedule(data);

				let { building_id, floor_id, room_id, meridian } = tableFilter;

				let dataSourceFacultyLoadFilteredTemp = data.filter(
					(f) =>
						(!building_id || f.building_id === building_id) &&
						(!floor_id || f.floor_id === floor_id) &&
						(!room_id || f.room_id === room_id) &&
						(!meridian || f.meridian === meridian)
				);

				setDataFacultyLoadScheduleFiltered(dataSourceFacultyLoadFilteredTemp);

				checkPermissions().then((res1) => {
					if (res1) {
						if (res1.publicStorage === "granted") {
							setFileStorage({
								foldername: "faculty_load_attendance",
								filename: "faculty_load_schedule_list",
								data,
								override: true,
							});
						}
					}
				});
			}
		},
		false
	);

	GET(
		"api/school_year?sort_field=id&sort_order=asc",
		"school_year_select",
		(res) => {
			if (res && res.data) {
				let data = res.data;

				setDataSchoolYear(data);

				checkPermissions().then((res1) => {
					if (res1) {
						if (res1.publicStorage === "granted") {
							setFileStorage({
								foldername: "faculty_load_attendance",
								filename: "school_year_list",
								data,
								override: true,
							});
						}
					}
				});
			}
		},
		false
	);

	GET(
		"api/semester?sort_field=id&sort_order=asc",
		"semester_select",
		(res) => {
			if (res && res.data) {
				let data = res.data;

				setDataSemester(data);

				checkPermissions().then((res1) => {
					if (res1) {
						if (res1.publicStorage === "granted") {
							setFileStorage({
								foldername: "faculty_load_attendance",
								filename: "semester_list",
								data,
								override: true,
							});
						}
					}
				});
			}
		},
		false
	);

	GET(
		"api/day_schedule?sort_field=id&sort_order=asc",
		"day_schedule_select",
		(res) => {
			if (res && res.data) {
				let data = res.data;

				setDataDaySchedules(data);

				checkPermissions().then((res1) => {
					if (res1) {
						if (res1.publicStorage === "granted") {
							setFileStorage({
								foldername: "faculty_load_attendance",
								filename: "day_schedule_list",
								data,
								override: true,
							});
						}
					}
				});
			}
		},
		false
	);

	GET(
		"api/building",
		"building_select",
		(res) => {
			if (res && res.data) {
				let data = res.data;

				setDataBuildings(data);

				checkPermissions().then((res1) => {
					if (res1) {
						if (res1.publicStorage === "granted") {
							setFileStorage({
								foldername: "faculty_load_attendance",
								filename: "building_list",
								data,
								override: true,
							});
						}
					}
				});
			}
		},
		false
	);

	GET(
		`api/floor`,
		"floor_select",
		(res) => {
			if (res && res.data) {
				let data = res.data;

				setDataFloors(data);

				checkPermissions().then((res1) => {
					if (res1) {
						if (res1.publicStorage === "granted") {
							setFileStorage({
								foldername: "faculty_load_attendance",
								filename: "floor_list",
								data,
								override: true,
							});
						}
					}
				});
			}
		},
		false
	);

	GET(
		`api/room`,
		"room_select",
		(res) => {
			if (res && res.data) {
				let data = res.data;

				setDataRooms(data);

				checkPermissions().then((res1) => {
					if (res1) {
						if (res1.publicStorage === "granted") {
							setFileStorage({
								foldername: "faculty_load_attendance",
								filename: "room_list",
								data,
								override: true,
							});
						}
					}
				});
			}
		},
		false
	);

	GET(
		`api/status?status_category_code=SC-01&from=PageFacultyLoadMonitoring`,
		"status_select",
		(res) => {
			if (res && res.data) {
				let data = res.data;

				setDataStatus(data);

				checkPermissions().then((res1) => {
					if (res1) {
						if (res1.publicStorage === "granted") {
							setFileStorage({
								foldername: "faculty_load_attendance",
								filename: "status_list",
								data,
								override: true,
							});
						}
					}
				});
			}
		},
		false
	);

	useEffect(() => {
		checkNetworkStatus().then((res) => {
			if (!res.connected) {
				readFile(
					"faculty_load_attendance/faculty_load_schedule_status.txt"
				).then((data) => {
					if (data) {
						setDataFacultyLoadScheduleStatus(data);
					}
				});
				readFile("faculty_load_attendance/profile_list.txt").then((data) => {
					if (data) {
						setDataFaculty(data);
					}
				});
				readFile("faculty_load_attendance/faculty_load_schedule_list.txt").then(
					(data) => {
						if (data) {
							setDataFacultyLoadSchedule(data);

							let { building_id, floor_id, room_id, meridian } = tableFilter;

							let dataSourceFacultyLoadFilteredTemp = data.filter(
								(f) =>
									(!building_id || f.building_id === building_id) &&
									(!floor_id || f.floor_id === floor_id) &&
									(!room_id || f.room_id === room_id) &&
									(!meridian || f.meridian === meridian)
							);

							setDataFacultyLoadScheduleFiltered(
								dataSourceFacultyLoadFilteredTemp
							);
						}
					}
				);
				readFile("faculty_load_attendance/school_year_list.txt").then(
					(data) => {
						if (data) {
							setDataSchoolYear(data);
						}
					}
				);
				readFile("faculty_load_attendance/semester_list.txt").then((data) => {
					if (data) {
						setDataSemester(data);
					}
				});
				readFile("faculty_load_attendance/day_schedule_list.txt").then(
					(data) => {
						if (data) {
							setDataDaySchedules(data);
						}
					}
				);
				readFile("faculty_load_attendance/building_list.txt").then((data) => {
					if (data) {
						setDataBuildings(data);
					}
				});
				readFile("faculty_load_attendance/floor_list.txt").then((data) => {
					if (data) {
						setDataFloors(data);
					}
				});
				readFile("faculty_load_attendance/room_list.txt").then((data) => {
					if (data) {
						setDataRooms(data);
					}
				});
				readFile("faculty_load_attendance/status_list.txt").then((data) => {
					if (data) {
						setDataStatus(data);
					}
				});
			}
		});

		return () => {};
	}, [tableFilter]);

	useEffect(() => {
		let tableFilterTemp = localStorage.getItem("tableFilter");

		if (tableFilterTemp) {
			tableFilterTemp = JSON.parse(tableFilterTemp);

			let dataRoomsTemp = [];

			if (tableFilterTemp.building_id) {
				let building_id = tableFilterTemp.building_id;
				let floor_id = tableFilterTemp.floor_id;

				if (floor_id) {
					dataRoomsTemp = dataRooms.filter(
						(x) => x.building_id === building_id && x.floor_id === floor_id
					);
				} else {
					dataRoomsTemp = dataRooms.filter(
						(x) => x.building_id === building_id
					);
				}

				setFilteredRooms(dataRoomsTemp);
			}
		}

		form.setFieldsValue({
			building_id: tableFilter.building_id ? tableFilter.building_id : null,
			floor_id: tableFilter.floor_id ? tableFilter.floor_id : null,
			room_id: tableFilter.room_id ? tableFilter.room_id : null,
			meridian: tableFilter.meridian ? tableFilter.meridian : null,
		});

		return () => {};
		// eslint-disable-next-line
	}, [dataRooms, tableFilter]);

	const onChangeTable = (pagination, filters, sorter) => {
		setTableFilter((ps) => ({
			...ps,
			sort_field: sorter.columnKey,
			sort_order: sorter.order ? sorter.order.replace("end", "") : null,
			page: 1,
			page_size: "50",
		}));
	};

	const handleFilterFacultyLoadSchedule = (field, value) => {
		let tableFilterCopy = { ...tableFilter };

		if (field === "building_id") {
			if (!value) {
				tableFilterCopy["building_id"] = null;
				tableFilterCopy["floor_id"] = null;
				tableFilterCopy["room_id"] = null;
				tableFilterCopy["meridian"] = null;
			} else {
				tableFilterCopy[field] = value ?? null;
			}
		} else {
			tableFilterCopy[field] = value ?? null;
		}

		let { building_id, floor_id, room_id, meridian, search, faculty_id } =
			tableFilterCopy;

		let dataRoomsTemp = [];

		if (field === "building_id") {
			dataRoomsTemp = dataRooms.filter((x) => x.building_id === value);

			floor_id = "";
			room_id = "";
			meridian = "";

			form.resetFields(["floor_id", "room_id", "meridian"]);
		} else if (field === "floor_id") {
			let floor_id = value;
			let building_id = form.getFieldValue("building_id");
			dataRoomsTemp = dataRooms.filter(
				(x) => x.floor_id === floor_id && x.building_id === building_id
			);

			room_id = "";
			meridian = "";

			form.resetFields(["room_id", "meridian"]);
		}

		setFilteredRooms(dataRoomsTemp);

		const lowerCaseSearch = search.toLowerCase();

		let dataSourceFacultyLoadFilteredTemp = dataFacultyLoadSchedule.filter(
			(f) =>
				(!building_id || f.building_id === building_id) &&
				(!floor_id || f.floor_id === floor_id) &&
				(!room_id || f.room_id === room_id) &&
				(!meridian || f.meridian === meridian) &&
				(!faculty_id || f.profile_id === faculty_id) &&
				(!search ||
					f.fullname.toLowerCase().includes(lowerCaseSearch) ||
					f.time_in.toLowerCase().includes(lowerCaseSearch) ||
					f.time_out.toLowerCase().includes(lowerCaseSearch) ||
					f.day_schedule.toLowerCase().includes(lowerCaseSearch) ||
					f.school_year.toLowerCase().includes(lowerCaseSearch) ||
					f.semester.toLowerCase().includes(lowerCaseSearch))
		);

		setDataFacultyLoadScheduleFiltered(dataSourceFacultyLoadFilteredTemp);

		tableFilterCopy.floor_id = floor_id;
		tableFilterCopy.room_id = room_id;
		tableFilterCopy.meridian = meridian;

		setTableFilter(tableFilterCopy);

		tableFilterCopy.faculty_id = "";
		localStorage.setItem("tableFilter", JSON.stringify(tableFilterCopy));
	};

	const handleTriggerDebounce = debounce((field, value) => {
		handleFilterFacultyLoadSchedule(field, value);
	}, 1000);

	const handleDebounce = useCallback(
		(field, value) => {
			handleTriggerDebounce(field, value);
		},
		[handleTriggerDebounce]
	);

	useTableScrollOnTop("tbl1", location.pathname);

	return (
		<PageFacultyMonitoringContext.Provider
			value={{
				dataFaculty,
				dataFacultyLoadSchedule,
				dataDaySchedules,
				dataSchoolYear,
				dataSemester,
				dataBuildings,
				dataFloors,
				dataRooms,
				dataStatus,
				toggleModalUpdateStatus,
				setToggleModalUpdateStatus,
				toggleModalFormFaculty,
				setToggleModalFormFaculty,
				tableFilter,
				setTableFilter,
				setDataFacultyLoadSchedule,
				setDataFacultyLoadScheduleStatus,
			}}
		>
			<Card>
				<Row gutter={[20, 20]}>
					<Col xs={24} sm={24} md={24} lg={24}>
						<Flex gap={15}>
							<Button
								className="btn-main-primary"
								onClick={() => {
									setToggleModalFormFaculty({ open: true, data: null });
								}}
								icon={<FontAwesomeIcon icon={faPlus} />}
							>
								Add Faculty Load
							</Button>

							{dataFacultyLoadScheduleStatus.length > 0 ? (
								<Badge
									size="medium"
									className="pulse"
									count={dataFacultyLoadScheduleStatus.length}
								>
									<Button
										onClick={() => {
											navigation("/faculty-monitoring/pending-list");
										}}
									>
										Faculty Load Status Pending Uplaod
									</Button>
								</Badge>
							) : (
								""
							)}

							{role() === "Super Admin" ? (
								<Button
									onClick={() => {
										readFile("faculty_load_attendance/faculty_load_status.txt")
											.then(async (res) => {
												if (res && res.data) {
													const blob = new Blob(
														[JSON.stringify(JSON.parse(res.data), null, 2)],
														{ type: "application/json" }
													);
													const url = URL.createObjectURL(blob);

													// Trigger download
													const a = document.createElement("a");
													a.href = url;
													a.download = "faculty_load_status.json";
													a.click();

													// Revoke the object URL to save memory
													URL.revokeObjectURL(url);
												}
											})
											.catch((err) => {
												notification.error({
													message: "Error",
													description: `An error occurred: ${
														err.message || err
													}`,
												});
											});
									}}
								>
									Download File Storage
								</Button>
							) : (
								""
							)}
						</Flex>
					</Col>

					<Col xs={24} sm={24} md={24} lg={24} className="filter-wrapper">
						<Form form={form}>
							<Row gutter={[20, 20]}>
								<Col xs={24} sm={12} md={8} lg={6}>
									<Form.Item name="building_id" className="m-0 1">
										<Select
											placeholder="Building"
											className="w-100 filter"
											showSearch
											allowClear
											options={dataBuildings.map((item) => {
												return {
													label: item.building,
													value: item.id,
												};
											})}
											onChange={(value) => {
												handleFilterFacultyLoadSchedule("building_id", value);
											}}
										/>
									</Form.Item>
								</Col>

								<Col xs={24} sm={12} md={8} lg={6}>
									<Form.Item name="floor_id" className="m-0 2">
										<Select
											placeholder="Floor"
											className="w-100 filter"
											allowClear
											options={dataFloors.map((item) => {
												return {
													label: item.floor,
													value: item.id,
												};
											})}
											onChange={(value) => {
												handleFilterFacultyLoadSchedule("floor_id", value);
											}}
										/>
									</Form.Item>
								</Col>

								<Col xs={24} sm={12} md={8} lg={6}>
									<Form.Item name="room_id" className="m-0">
										<Select
											placeholder="Room"
											className="w-100 filter"
											allowClear
											options={filteredRooms.map((item) => {
												return {
													label: item.room_code,
													value: item.id,
												};
											})}
											onChange={(value) => {
												handleFilterFacultyLoadSchedule("room_id", value);
												form.resetFields(["meridian"]);
											}}
										/>
									</Form.Item>
								</Col>

								<Col xs={24} sm={12} md={8} lg={6}>
									<Form.Item name="meridian" className="m-0">
										<Select
											placeholder="Meridian"
											className="w-100 filter"
											allowClear
											options={[
												{ label: "AM", value: "AM" },
												{ label: "PM", value: "PM" },
											]}
											onChange={(value) => {
												handleFilterFacultyLoadSchedule("meridian", value);
											}}
										/>
									</Form.Item>
								</Col>

								<Col xs={24} sm={12} md={8} lg={6}>
									<Form.Item name="search" className="m-0 form-item-search">
										<Input.Search
											placeholder="Search"
											className="w-100 filter"
											onChange={(value) => {
												handleDebounce("search", value.target.value);
											}}
										/>
									</Form.Item>
								</Col>
								<Col xs={24} sm={12} md={8} lg={6}>
									<Form.Item name="faculty_id" className="m-0">
										<Select
											placeholder="Faculty Name"
											className="w-100 filter"
											allowClear
											showSearch
											filterOption={(input, option) => {
												// console.log("option", option);
												return (
													option.label
														.toLowerCase()
														.indexOf(input.toLowerCase()) >= 0
												);
											}}
											options={dataFaculty}
											onChange={(value) => {
												handleFilterFacultyLoadSchedule("faculty_id", value);
											}}
										/>
									</Form.Item>
								</Col>
							</Row>
						</Form>
					</Col>

					<Col xs={24} sm={24} md={24}>
						<Table
							id="tbl1"
							className="ant-table-default ant-table-striped"
							dataSource={dataFacultyLoadScheduleFiltered}
							rowKey={(record) => record.id}
							pagination
							bordered={false}
							onChange={onChangeTable}
							scroll={{ x: "max-content" }}
							// onRow={(record) => {
							// 	return {
							// 		onClick: () => {
							// 			setToggleModalUpdateStatus({ open: true, data: record });
							// 		},
							// 	};
							// }}
							sticky
						>
							<Table.Column
								title="Name"
								key="fullname"
								sorter={(a, b) => a.fullname.localeCompare(b.fullname)}
								render={(text, record) => (
									<Button
										type="link"
										className="color-1"
										onClick={() => {
											setToggleModalUpdateStatus({ open: true, data: record });
										}}
									>
										{record.fullname}
									</Button>
								)}
								width={120}
							/>

							<Table.Column
								title="Schedule"
								key="schedule"
								render={(_, record) => {
									let schedule = `${record.time_in} - ${record.time_out} ${record.meridian} - ${record.day_schedule}`;

									return schedule;
								}}
							/>

							<Table.Column
								title="Room"
								key="room_code"
								dataIndex="room_code"
								sorter={(a, b) => a.room_code.localeCompare(b.room_code)}
							/>
							<Table.Column
								title="Subject"
								key="subject_code"
								dataIndex="subject_code"
								sorter={(a, b) => a.subject_code.localeCompare(b.subject_code)}
							/>
							<Table.Column
								title="School Year"
								key="school_year"
								dataIndex="school_year"
								sorter={(a, b) => a.school_year.localeCompare(b.school_year)}
							/>
							<Table.Column
								title="Semester"
								key="semester"
								dataIndex="semester"
								sorter={(a, b) => a.semester.localeCompare(b.semester)}
							/>
						</Table>
					</Col>
				</Row>
			</Card>

			<ModalFormUpdateStatus />
			<ModalFormFacultyLoad />
		</PageFacultyMonitoringContext.Provider>
	);
}
